import { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { providerAPI } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { Search, Users, FileText, Calendar, Loader2, Eye, UserPlus, Plus } from 'lucide-react'
import { AddPatientModal, type AddPatientFormData } from '@/components/provider/AddPatientModal'
import { AddPatientRecordModal, type AddPatientRecordFormData } from '@/components/provider/AddPatientRecordModal'

interface Patient {
  id: string
  fullName: string
  email: string
  dateOfBirth?: string
  lastActivity?: string
  recordsCount: number
  sharedWithProvider: boolean
}

export default function ProviderPatientsPage() {
  const { toast } = useToast()
  const [patients, setPatients] = useState<Patient[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isAddRecordModalOpen, setIsAddRecordModalOpen] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)

  useEffect(() => {
    fetchPatients()
  }, [])

  const fetchPatients = async () => {
    try {
      setIsLoading(true)
      const response = await providerAPI.getPatients({ search: searchTerm })
      if (response.success) {
        // Map the response data to match our Patient interface
        const patientsData = response.data || []
        const mappedPatients = patientsData.map((p: any) => ({
          id: p.id,
          fullName: `${p.firstName || p.first_name || ''} ${p.lastName || p.last_name || ''}`.trim() || 'Unknown',
          email: p.email || '',
          dateOfBirth: p.dateOfBirth || p.date_of_birth,
          lastActivity: p.lastVisitAt || p.last_visit_date,
          recordsCount: p.recordCount || p.records_count || 0,
          sharedWithProvider: p.patientType === 'linked' || p.patient_type === 'linked' || p.linkedCareVaultUserId !== null || p.carevault_user_id !== null
        }))
        setPatients(mappedPatients)
      }
    } catch (error: any) {
      console.error('Failed to fetch patients:', error)
      setPatients([])
      // Only show toast for non-403 errors (403 means no provider profile yet)
      if (error.response?.status !== 403) {
        toast({
          title: "Could not load patients",
          description: "Please try again later.",
          variant: "destructive",
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    fetchPatients()
  }

  const handleAddPatient = async (data: AddPatientFormData) => {
    try {
      const response = await providerAPI.createPatient({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || undefined,
        phone: data.phone || undefined,
        dateOfBirth: data.dateOfBirth || undefined,
      })

      if (response.success) {
        toast({
          title: "Patient added",
          description: `${data.firstName} ${data.lastName} has been added to your patient roster.`,
        })
        setIsAddModalOpen(false)
        // Refresh the patient list
        fetchPatients()
      }
    } catch (error: any) {
      console.error('Failed to create patient:', error)
      toast({
        title: "Failed to add patient",
        description: error.response?.data?.message || "Please try again later.",
        variant: "destructive",
      })
      throw error // Re-throw to let the modal know submission failed
    }
  }

  const handleAddRecord = async (data: AddPatientRecordFormData) => {
    try {
      const response = await providerAPI.createPatientRecord({
        patientId: data.patientId,
        recordType: data.recordType,
        title: data.title,
        content: data.content,
      })

      if (response.success) {
        toast({
          title: "Record created",
          description: `Record "${data.title}" has been created successfully.`,
        })
        setIsAddRecordModalOpen(false)
        setSelectedPatient(null)
        // Refresh the patient list to update record counts
        fetchPatients()
      }
    } catch (error: any) {
      console.error('Failed to create record:', error)
      toast({
        title: "Failed to create record",
        description: error.response?.data?.message || "Please try again later.",
        variant: "destructive",
      })
      throw error
    }
  }

  const openAddRecordModal = (patient: Patient) => {
    setSelectedPatient(patient)
    setIsAddRecordModalOpen(true)
  }

  const filteredPatients = patients.filter(patient =>
    patient.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Patients</h1>
          <p className="text-gray-600">Manage your patient roster and view shared records</p>
        </div>
        <Button 
          onClick={() => setIsAddModalOpen(true)}
          className="bg-[#D64463] hover:bg-[#0F172A] text-white"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add Patient
        </Button>
      </div>

      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search patients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button type="submit" variant="outline">Search</Button>
      </form>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#D64463]" />
        </div>
      ) : filteredPatients.length > 0 ? (
        <div className="grid gap-4">
          {filteredPatients.map((patient) => (
            <Card key={patient.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#D64463] to-[#9C27B0] flex items-center justify-center text-white font-semibold">
                      {patient.fullName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{patient.fullName}</h3>
                      <p className="text-sm text-gray-500">{patient.email || 'No email provided'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-gray-500">
                        <FileText className="h-4 w-4" />
                        <span className="text-sm">{patient.recordsCount} records</span>
                      </div>
                    </div>
                    
                    {patient.lastActivity && (
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-gray-500">
                          <Calendar className="h-4 w-4" />
                          <span className="text-sm">
                            {new Date(patient.lastActivity).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    <Badge className={patient.sharedWithProvider ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                      {patient.sharedWithProvider ? 'Linked' : 'Managed'}
                    </Badge>
                    
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openAddRecordModal(patient)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Record
                    </Button>
                    
                    <Button variant="outline" size="sm">
                      <Eye className="h-4 w-4 mr-1" />
                      View Records
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No patients yet</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              Add patients to your roster to manage their health records and track their care.
            </p>
            <Button 
              onClick={() => setIsAddModalOpen(true)}
              className="bg-[#D64463] hover:bg-[#0F172A] text-white"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Your First Patient
            </Button>
          </CardContent>
        </Card>
      )}

      <AddPatientModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddPatient}
      />

      {selectedPatient && (
        <AddPatientRecordModal
          isOpen={isAddRecordModalOpen}
          onClose={() => {
            setIsAddRecordModalOpen(false)
            setSelectedPatient(null)
          }}
          onSubmit={handleAddRecord}
          patientId={selectedPatient.id}
          patientName={selectedPatient.fullName}
        />
      )}
    </div>
  )
}
